import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import { type ChangeEvent, type MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';

export type DuplicateAudienceDialogErrorSetter = (message: string | null) => void;

type DuplicateAudienceDialogContentProps = {
  audience: Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'> & {
    account?: Pick<NonNullable<GetAudiencesListQuery['audience'][number]['account']>, 'name'> | null;
  };
  nameRef: MutableRefObject<string>;
  toogleConfirmationDisabled: (disabled: boolean) => void;
  errorSetterRef?: MutableRefObject<DuplicateAudienceDialogErrorSetter | null>;
};

export const DuplicateAudienceDialogContent = ({
  audience,
  nameRef,
  toogleConfirmationDisabled,
  errorSetterRef,
}: DuplicateAudienceDialogContentProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [externalError, setExternalError] = useState<string | null>(null);

  const validate = useCallback(
    (value: string) => {
      if (value === '') {
        toogleConfirmationDisabled(true);
        return;
      }

      if (value === audience.name) {
        setError(t('Name of duplicated audience has to be different than current'));
        toogleConfirmationDisabled(true);
        return;
      }

      setError(null);
      toogleConfirmationDisabled(false);
    },
    [audience.name, toogleConfirmationDisabled, t]
  );

  const helperText = error ?? externalError;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      nameRef.current = value;
      if (externalError) {
        setExternalError(null);
      }
      validate(value);
    },
    [externalError, nameRef, validate]
  );

  useEffect(() => {
    if (!errorSetterRef) {
      return;
    }
    errorSetterRef.current = setExternalError;

    return () => {
      errorSetterRef.current = null;
    };
  }, [errorSetterRef]);

  return (
    <DialogContent dividers sx={{ pb: 4 }}>
      <Typography variant="body1" gutterBottom>
        {t('You are duplicating audience:')} <b>{audience.name}</b>
      </Typography>
      <Typography
        variant="body1"
        gutterBottom
        sx={{
          mb: 2,
        }}
      >
        {t('Partner:')} <b>{audience.account?.name}</b>
      </Typography>
      <TextField
        data-test="new-name"
        label={t('New name')}
        fullWidth
        autoFocus
        onChange={handleChange}
        error={!!helperText}
        helperText={helperText}
      />
    </DialogContent>
  );
};
